/* Guitar tuner. Pitch comes from a longer analysis window than the exercises use (steadier on low
   strings) and goes through the Tuner model, which holds, smooths and locks the reading. */
import { useEffect, useRef, useState } from 'react';
import { audio } from '../../core/audio';
import { debugEnabled, exposeDebug } from '../../core/debug';
import { detectPitch } from '../../core/dsp';
import { useMicFrame } from '../../core/mic';
import { tunerGate } from '../../core/mic/floor';
import { addXp, award, setSetting, useSettings } from '../../core/store';
import { noteParts, openString, shortNote } from '../../core/theory';
import { toast } from '../../core/toast';
import { Tuner, type TunerReading } from '../../core/tuner/tuner';
import { Gauge, type GaugeHandle } from '../../ui/Gauge';
import { Icon, type IconName } from '../../ui/icons';
import { MicGate } from '../exercise/MicBlock';
import { ToolShell } from './ToolShell';

const STRINGS = [6, 5, 4, 3, 2, 1];
const A4_OPTIONS = [432, 435, 438, 440, 442, 444];

type HintDir = 'idle' | 'up' | 'down' | 'ok' | 'off';

/** What the learner should do now, for a reading and the strings already tuned. */
export function hintFor(rd: TunerReading, tuned: ReadonlySet<number>): { dir: HintDir; text: string; icon?: IconName } {
  if (rd.state === 'idle' || rd.cents == null)
    return { dir: 'idle', text: 'Bir teli çal ve sesini bırak, ibre yerini bulsun.' };
  if (rd.offString) return { dir: 'off', text: 'Bu bir boş tel değil: akort için tellerden birini boş çal.' };
  if (rd.inTune) {
    if (rd.string != null && (tuned.has(rd.string) || rd.confirm >= 1))
      return { dir: 'ok', text: 'Akortlu, böyle bırak.', icon: 'check' };
    // the string faded before it was confirmed: holding on is no longer possible
    if (rd.state === 'held')
      return { dir: 'ok', text: 'Tam ortadaydı. Onaylamak için teli yeniden çal.', icon: 'check' };
    return { dir: 'ok', text: 'Tam ortada, sesi biraz tut…', icon: 'check' };
  }
  const c = rd.cents;
  const much = Math.abs(c) > 25;
  return c < 0
    ? { dir: 'up', text: much ? 'Pes: teli sıkılaştır.' : 'Pes: teli biraz sıkılaştır.', icon: 'up' }
    : { dir: 'down', text: much ? 'Tiz: teli gevşet.' : 'Tiz: teli biraz gevşet.', icon: 'down' };
}

export function TunerPage() {
  const settings = useSettings();
  const [manual, setManual] = useState<number | null>(null);
  const [active, setActive] = useState<number | null>(null);
  const [tuned, setTuned] = useState<ReadonlySet<number>>(() => new Set());
  const [hint, setHint] = useState<{ dir: HintDir; text: string; icon?: IconName }>({
    dir: 'idle',
    text: 'Bir teli çal ve sesini bırak, ibre yerini bulsun.',
  });
  const gauge = useRef<GaugeHandle>(null);
  const root = useRef<HTMLDivElement>(null);
  const confirmBar = useRef<HTMLSpanElement>(null);
  const model = useRef<Tuner>(null);
  if (!model.current) model.current = new Tuner({ a4: settings.a4 });
  const tunedRef = useRef(tuned);
  tunedRef.current = tuned;
  const activeRef = useRef(active);
  activeRef.current = active;
  const hintRef = useRef(hint);
  hintRef.current = hint;

  useEffect(() => {
    model.current?.setOptions({ a4: settings.a4, string: manual });
  }, [settings.a4, manual]);

  useMicFrame((f) => {
    const tuner = model.current;
    if (!tuner) return;
    const W = f.sr > 60000 ? 8192 : 4096;
    const r = detectPitch(f.buf.subarray(f.buf.length - W), f.sr, { minF: 60, maxF: 1000 });
    // A clear pitch stays measurable well below the exercise gate, so the tuner listens closer to the
    // noise floor and keeps reading the string while it fades (the floor never learns from that band).
    const gate = tunerGate(f.floor) / (settings.micSensitivity || 1);
    const rd = tuner.push({
      t: f.t,
      freq: r.freq,
      clarity: r.clarity,
      rms: r.rms,
      gate,
      gated: f.gated,
      hf: f.hf,
    });
    if (debugEnabled()) exposeDebug('tunerFrame', { ...r, t: f.t, gate, gated: f.gated, hf: f.hf });
    const el = root.current;
    if (el) {
      el.dataset.state = rd.state;
      el.dataset.string = rd.string == null ? '' : String(rd.string);
      el.dataset.cents = rd.cents == null ? '' : rd.cents.toFixed(1);
      el.dataset.inTune = rd.inTune ? 'true' : 'false';
      el.dataset.confirm = rd.confirm.toFixed(2);
    }
    if (confirmBar.current) confirmBar.current.style.width = rd.offString ? '0%' : `${Math.round(rd.confirm * 100)}%`;
    if (rd.state === 'idle' || rd.cents == null || rd.target == null) {
      gauge.current?.update({ active: false, main: '—', sub: '' });
    } else {
      const c = rd.cents;
      const held = rd.state === 'held';
      const main = rd.offString ? shortNote(rd.target) : noteParts(rd.target).main;
      const sub = rd.offString
        ? 'tel dışı nota'
        : `${rd.string}. tel${Math.abs(c) > 50 ? ` · ${c < 0 ? 'çok pes, sıkılaştır' : 'çok tiz, gevşet'}` : ''}${held ? ' · son ölçüm' : ''}`;
      gauge.current?.update({
        active: !held,
        held,
        main,
        sub,
        cents: c,
        hz: rd.hz,
        inTune: rd.inTune,
        smooth: false,
      });
      if (!rd.offString && rd.string !== activeRef.current) setActive(rd.string);
      if (rd.offString && activeRef.current != null && manual == null) setActive(null);
    }
    const h = hintFor(rd, tunedRef.current);
    if (h.dir !== hintRef.current.dir || h.text !== hintRef.current.text) setHint(h);
    const sn = rd.confirmed;
    if (sn != null && !tunedRef.current.has(sn)) {
      const next = new Set(tunedRef.current).add(sn);
      tunedRef.current = next;
      setTuned(next);
      toast(`${sn}. tel akortlu`, { tone: 'good', icon: 'check' });
      if (next.size === 6) {
        award('akort');
        addXp(10, 'gitar');
      }
    }
  });

  return (
    <ToolShell
      title="Akort aleti"
      desc="Standart akort: {{pc:E}} {{pc:A}} {{pc:D}} {{pc:G}} {{pc:B}} {{pc:E}}. Teli çal, ibre ortaya gelene kadar burguyu çevir."
      icon="tuner"
    >
      <MicGate text="Akort için mikrofon gerekir. Mikrofonsuz da tellere dokunarak referans sesleri dinleyip kulakla akort edebilirsin." />
      <div className="tuner" ref={root} data-testid="tuner" data-state="idle">
        <Gauge ref={gauge} />
        <div className="tuner-hint" data-dir={hint.dir} aria-live="polite">
          {hint.icon ? <Icon name={hint.icon} /> : null}
          <span>{hint.text}</span>
        </div>
        <div className="tuner-confirm" aria-hidden="true">
          <span ref={confirmBar} />
        </div>
        <div className="str-row">
          {STRINGS.map((sn) => {
            const m = openString(sn);
            return (
              <button
                key={sn}
                type="button"
                data-sn={sn}
                className={`str-btn${(manual ?? active) === sn ? ' active' : ''}${tuned.has(sn) ? ' tuned' : ''}`}
                onClick={async () => {
                  await audio.resume();
                  const next = manual === sn ? null : sn;
                  setManual(next);
                  setActive(sn);
                  audio.playNote(m, { inst: 'guitar', dur: 2.5 });
                }}
              >
                <span className="sb-no">{sn}. tel</span>
                <span className="sb-name">{shortNote(m)}</span>
              </button>
            );
          })}
        </div>
        <p className="mb-note">
          {manual
            ? `${manual}. tel seçili. Otomatik algılamaya dönmek için tekrar dokun.`
            : 'Otomatik: çaldığın tel kendiliğinden bulunur. Referans sesi duymak için bir tele dokun.'}
        </p>
        <label className="field">
          <span>Referans frekansı</span>
          <select
            id="tuner-a4"
            value={String(settings.a4)}
            onChange={(e) => setSetting('a4', Number.parseInt(e.target.value, 10))}
          >
            {A4_OPTIONS.map((v) => (
              <option key={v} value={String(v)}>
                La4 = {v} Hz
              </option>
            ))}
          </select>
          <small>Çoğu müzik 440 Hz'e göre akortludur. Bir kayıtla birlikte çalacaksan ona uyacak değeri seç.</small>
        </label>
      </div>
    </ToolShell>
  );
}
